import React from "react";
import {
    Archive24Regular,
    Delete24Regular,
    Folder20Regular,
    FolderArrowRight24Regular,
    MailRead24Regular,
    MailUnread24Regular,
    MoreHorizontal24Regular,
} from "@fluentui/react-icons";
import BottomSheet from "../../calendar/BottomSheet";
import "./mailMobil.css";

export interface OrdnerZiel {
    key: string;
    label: string;
    depth: number;
}

export interface WeitereAktion {
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
    disabled?: boolean;
}

export interface MailAuswahlLeisteProps {
    anzahl: number;
    /** `true` = der Lese-Knopf markiert als gelesen, sonst als ungelesen. */
    alsGelesen: boolean;
    onLesestatus: () => void;
    /**
     * Ziele für „Verschieben" — die Ordner des gemeinsamen Postfachs. `null`,
     * wenn die Auswahl aus mehreren Postfächern stammt (Suchtreffer): dann
     * gibt es kein gemeinsames Ziel.
     */
    ordner: OrdnerZiel[] | null;
    onVerschieben: (ziel: OrdnerZiel) => void;
    onArchivieren: () => void;
    /** Fehlt im Papierkorb (Löschen wäre dort ein No-Op). */
    onLoeschen?: () => void;
    /**
     * „…": bei EINER Mail öffnet das das Kontextmenü des Desktops mit ALLEN
     * Aktionen (Vorgang, Kontakt, Schaden, Regel, …). Bei mehreren steht ein
     * Sheet mit den Aktionen, die für mehrere Mails gehen.
     */
    onMehrEinzeln?: () => void;
    weitere: WeitereAktion[];
}

/**
 * Aktionsleiste im Auswahlmodus (Outlook iOS): schwebt über der
 * App-Navigation, die darunter erreichbar bleibt. Dieselben Aktionen wie das
 * Rechtsklick-Menü am Desktop — die häufigen direkt, der Rest unter „…".
 */
export const MailAuswahlLeiste: React.FC<MailAuswahlLeisteProps> = ({
    anzahl,
    alsGelesen,
    onLesestatus,
    ordner,
    onVerschieben,
    onArchivieren,
    onLoeschen,
    onMehrEinzeln,
    weitere,
}) => {
    const [sheet, setSheet] = React.useState<"verschieben" | "mehr" | null>(null);
    const leer = anzahl === 0;
    return (
        <>
            <div className="mm-leiste" role="toolbar" aria-label="Aktionen für die Auswahl">
                <button type="button" className="mm-leiste-btn" disabled={leer} onClick={onLesestatus}>
                    {alsGelesen ? <MailRead24Regular /> : <MailUnread24Regular />}
                    <span>{alsGelesen ? "Gelesen" : "Ungelesen"}</span>
                </button>
                <button
                    type="button"
                    className="mm-leiste-btn"
                    disabled={leer}
                    onClick={() => setSheet("verschieben")}
                >
                    <FolderArrowRight24Regular />
                    <span>Verschieben</span>
                </button>
                <button type="button" className="mm-leiste-btn" disabled={leer} onClick={onArchivieren}>
                    <Archive24Regular />
                    <span>Archivieren</span>
                </button>
                {onLoeschen && (
                    <button type="button" className="mm-leiste-btn" disabled={leer} onClick={onLoeschen}>
                        <Delete24Regular />
                        <span>Löschen</span>
                    </button>
                )}
                <button
                    type="button"
                    className="mm-leiste-btn"
                    disabled={leer}
                    aria-label="Weitere Aktionen"
                    onClick={() => {
                        if (anzahl === 1 && onMehrEinzeln) onMehrEinzeln();
                        else setSheet("mehr");
                    }}
                >
                    <MoreHorizontal24Regular />
                    <span>Mehr</span>
                </button>
            </div>

            {/* `.cal-rd` als Anker der `.ct-sheet-*`-Regeln (wie im Kalender). */}
            <div className="cal-rd">
                <BottomSheet
                    open={sheet === "verschieben"}
                    title={anzahl === 1 ? "Verschieben nach" : `${anzahl} Mails verschieben nach`}
                    onClose={() => setSheet(null)}
                >
                    {ordner === null ? (
                        <div className="mm-sheet-hinweis">
                            Die ausgewählten Mails liegen in verschiedenen Postfächern. Bitte nur Mails aus
                            einem Postfach zusammen verschieben.
                        </div>
                    ) : (
                        ordner.map((o) => (
                            <button
                                key={o.key}
                                type="button"
                                className="mm-sheet-zeile"
                                style={{ paddingLeft: 12 + o.depth * 16 }}
                                onClick={() => {
                                    setSheet(null);
                                    onVerschieben(o);
                                }}
                            >
                                <Folder20Regular />
                                <span className="mm-label">{o.label}</span>
                            </button>
                        ))
                    )}
                </BottomSheet>
                <BottomSheet open={sheet === "mehr"} title="Weitere Aktionen" onClose={() => setSheet(null)}>
                    {weitere.map((a) => (
                        <button
                            key={a.label}
                            type="button"
                            className="mm-sheet-zeile"
                            disabled={a.disabled}
                            onClick={() => {
                                setSheet(null);
                                a.onClick();
                            }}
                        >
                            {a.icon}
                            <span className="mm-label">{a.label}</span>
                        </button>
                    ))}
                    {anzahl > 1 && (
                        <div className="mm-sheet-hinweis">
                            Vorgang erstellen oder zuweisen, Kontakt zuweisen, Schaden zuordnen und Regel
                            erstellen gehen für eine einzelne Mail — dafür nur eine auswählen.
                        </div>
                    )}
                </BottomSheet>
            </div>
        </>
    );
};
